import * as PIXI from 'pixi.js'
import gsap from 'gsap'
import AssetsManager from '../../../pixi/AssetsManager'
import MyApplication from '../../../pixi/MyApplication'
import CardModel from '../data/CardModel'
import { CARD_TYPE } from '../constant/CardType'
import Stacs from './Stacs'

/**
 * CardViewクラス
 */
class CardView extends PIXI.Container {
  // ---------------------------------------------------------------------------------------------------------
  // --	VARS
  // ---------------------------------------------------------------------------------------------------------

  //PIXI.Application
  private _app: PIXI.Application

  //モデル
  private _model: CardModel

  //コンテナ
  private _container: PIXI.Container

  //表
  private _omote: PIXI.Container

  //裏
  private _ura: PIXI.Container

  //フラグ
  private _isDragging: boolean = false

  //ドラッグ用変数
  private _dragDiff: PIXI.Point = new PIXI.Point()

  //タッチID
  private _touchID: number = -1

  //めくれているかどうかのフラグ
  private _isOmote: boolean = false
  public get isOmote(): boolean {
    return this._isOmote
  }

  //落下中フラグ
  private _isFalling: boolean = false

  //速度
  private _speedY: number = 0
  private _speedR: number = 0

  //アップデートメソッド
  private _update: () => void

  // ---------------------------------------------------------------------------------------------------------
  // --	CardView
  // ---------------------------------------------------------------------------------------------------------

  /**
   * コンストラクタ
   */
  constructor(model: CardModel) {
    super()

    //モデル保持
    this._model = model

    //アプリケーション参照
    this._app = MyApplication.app

    //スケール
    this.scale.set(3)

    //コンテナ
    this._container = new PIXI.Container()
    this.addChild(this._container)

    //表
    this._omote = new PIXI.Container()
    this._container.addChild(this._omote)

    //ベースのスプライト
    const base = AssetsManager.instance.createSpriteByName('card_omote.png')
    base.anchor.set(0.5)
    this._omote.addChild(base)

    //マーク
    const mark: PIXI.AnimatedSprite = AssetsManager.instance.createMovieClipFromAnimation(
      'ss',
      'mark'
    )
    mark.anchor.set(0.5)
    mark.x = -6
    mark.y = -12
    mark.gotoAndStop(model.type)
    this._omote.addChild(mark)

    //数字
    const numStr: string =
      model.type === CARD_TYPE.CLUB || model.type === CARD_TYPE.SPADE ? 'b' : 'r'
    const number: PIXI.AnimatedSprite = AssetsManager.instance.createMovieClipFromAnimation(
      'ss',
      'num_' + numStr
    )
    number.anchor.set(0.5)
    number.x = 6
    number.y = -12
    number.gotoAndStop(model.number - 1)
    this._omote.addChild(number)
    const numberLarge: PIXI.AnimatedSprite = AssetsManager.instance.createMovieClipFromAnimation(
      'ss',
      'num_' + numStr
    )
    numberLarge.anchor.set(0.5)
    numberLarge.scale.set(2)
    numberLarge.y = 5
    numberLarge.gotoAndStop(model.number - 1)
    this._omote.addChild(numberLarge)

    //裏
    this._ura = new PIXI.Container()
    this._container.addChild(this._ura)

    //ベースのスプライト
    const baseUra = AssetsManager.instance.createSpriteByName('card_ura.png')
    baseUra.anchor.set(0.5)
    this._ura.addChild(baseUra)

    //アップデート
    this._update = this.update.bind(this)
  }

  /**
   * ひっくり返す
   */
  public flip(
    omote: boolean,
    duration: number = 0.1,
    toggle: boolean = false,
    callBack: () => void = null
  ): void {
    //めくれている場合は無視
    if (toggle) {
      omote = !this._isOmote
    } else if (this._isOmote === omote) {
      return
    }
    this._isOmote = omote

    gsap.to(this._container.scale, {
      x: 0,
      duration: duration,
      ease: 'power1.inOut',
      onStart: () => {
        if (!this._isOmote) this.activate(false)
      },
      onComplete: () => {
        this._omote.visible = omote
        this._ura.visible = !omote
        if (this._isOmote) this.activate(true)
        gsap.to(this._container.scale, {
          x: 1,
          duration: duration,
          onComplete: () => {
            if (callBack) callBack()
          },
        })
      },
    })
  }

  // ---------------------------------------------------------------------------------------------------------
  // --	UTIL
  // ---------------------------------------------------------------------------------------------------------

  /**
   * 親を取得
   */
  public getParentAsStacs(): Stacs {
    if (this.parent.parent instanceof Stacs) {
      return this.parent.parent
    } else {
      return null
    }
  }

  // ---------------------------------------------------------------------------------------------------------
  // --	FALL
  // ---------------------------------------------------------------------------------------------------------

  fall(): void {
    //フラグ
    this._isFalling = true

    //初期サイズ
    this.scale.set(2 + Math.random() * 5)
    this.zIndex = this.scale.x

    //初期位置
    this.x = 50 + Math.random() * 540
    this.y = -this.height

    //落下速度
    this._speedY = 5 * (this.scale.x / 10)

    //回転速度
    this._speedR = Math.random() * 0.1 - 0.05

    //自動でひっくり返す
    this.autoFlip()

    //アップデート
    MyApplication.app.ticker.remove(this._update)
    MyApplication.app.ticker.add(this._update)
  }

  /**
   * ひっくり返す
   */
  autoFlip(): void {
    if (!this._isFalling) return
    this.flip(true, 1.0, true, () => {
      this.autoFlip()
    })
  }

  /**
   * アップデート
   */
  private update(): void {
    //移動
    this.y += this._speedY
    this.rotation += this._speedR

    //消す
    if (this.y >= MyApplication.app.screen.height + this.height) {
      this.stopFall()
    }
  }

  /**
   * 落下停止
   */
  stopFall(): void {
    this._isFalling = false
    MyApplication.app.ticker.remove(this._update)
    this.parent.removeChild(this)
  }

  // ---------------------------------------------------------------------------------------------------------
  // --	INPUT
  // ---------------------------------------------------------------------------------------------------------

  /**
   * アクティベート
   */
  activate(bool) {
    //入力受付設定
    this.interactive = bool

    //ポインター
    this.cursor = this.interactive ? 'pointer' : 'default'
  }

  /**
   * ドラッグ開始
   */
  onDragStart(e) {
    // if (e.data.pointerType == 'touch' && e.data.originalEvent.touches.length > 1) return

    //タッチID保持
    if (this._touchID != -1) return
    this._touchID = e.data.identifier

    //タッチ座標
    const p = e.data.global

    //フラグ立てる
    this._isDragging = true

    const global = this.toGlobal(new PIXI.Point(0, 0))

    //タッチ座標のズレを保存
    this._dragDiff.x = p.x - global.x
    this._dragDiff.y = p.y - global.y

    //イベント発行
    this.emit('DRAG_START' as any, this)
  }

  /**
   * ドラッグ
   */
  onDrag(e) {
    if (!this._isDragging) return

    //IDが違う場合は無視
    if (this._touchID != e.data.identifier) return

    //タッチ座標
    const p = e.data.global

    //ドラッグ
    this.x = p.x - this._dragDiff.x
    this.y = p.y - this._dragDiff.y

    //イベント発行
    this.emit('DRAG' as any, this)
  }

  /**
   * ドラッグ終了
   */
  onDragEnd(e) {
    if (!this._isDragging) return

    //IDリセット
    if (this._touchID != e.data.identifier) return
    this._touchID = -1

    //フラグを戻す
    this._isDragging = false

    //イベント発行
    this.emit('DRAG_END' as any, this)
  }

  // ---------------------------------------------------------------------------------------------------------
}

export default CardView
